import React, { useState, useRef, useEffect } from 'react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import emailjs from '@emailjs/browser';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
  Fade,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  IconButton,
  Divider,
  Tooltip,
  Zoom,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FactoryIcon from '@mui/icons-material/Factory';
import EngineeringIcon from '@mui/icons-material/Engineering';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import LanguageIcon from '@mui/icons-material/Language';

const contactCards = [
  {
    icon: <LocationOnIcon />,
    title: "Factory Location",
    details: [
      "E-143, Sector-63,",
      "Noida, Gautam Buddha Nagar,",
      "Uttar Pradesh - 201301, India"
    ],
    link: "https://maps.google.com/?q=E-143+Sector-63+Noida",
    linkText: "View on Google Maps",
    color: "#1a472a"
  },
  {
    icon: <FactoryIcon />,
    title: "Manufacturing Unit",
    details: [
      "State-of-the-art facility",
      "ISO 9001:2015 Certified",
      "Advanced CNC Machinery"
    ],
    color: "#4caf50"
  },
  {
    icon: <AccessTimeIcon />,
    title: "Business Hours",
    details: [
      "Monday - Saturday: 9:00 AM - 6:00 PM",
      "Sunday: Closed",
      "Emergency Support Available 24/7"
    ],
    color: "#2196f3"
  },
  {
    icon: <EngineeringIcon />,
    title: "Technical Support",
    details: [
      "Expert Engineering Team",
      "Quality Assurance Department",
      "Rapid Response Unit"
    ],
    color: "#ff9800"
  }
];

const whyConnectCards = [
  {
    icon: <PrecisionManufacturingIcon />,
    title: "Precision Engineering Excellence",
    description: "Partner with us for high-precision components that meet the most demanding specifications. Our ISO 9001:2015 certified facility ensures consistent quality.",
    points: [
      "Advanced CNC machining capabilities",
      "Tolerance levels up to ±0.01mm",
      "100% quality inspection"
    ],
    color: "#1a472a"
  },
  {
    icon: <EngineeringIcon />,
    title: "Expert Consultation",
    description: "Benefit from our decades of engineering expertise. Our team provides comprehensive technical consultation for your manufacturing needs.",
    points: [
      "Dedicated project managers",
      "Design optimization support",
      "Cost-effective solutions"
    ],
    color: "#4caf50"
  },
  {
    icon: <FactoryIcon />,
    title: "State-of-the-Art Manufacturing",
    description: "Experience manufacturing excellence with our modern facility equipped with the latest technology and automated systems.",
    points: [
      "24/7 production capability",
      "Quick turnaround time",
      "Flexible batch sizes"
    ],
    color: "#2196f3"
  }
];

const industriesServed = [
  {
    title: "Automotive",
    details: "Precision components for automotive systems",
    icon: "🚗"
  },
  {
    title: "Aerospace",
    details: "High-precision parts for aerospace applications",
    icon: "✈️"
  },
  {
    title: "Defense",
    details: "Critical components for defense equipment",
    icon: "🛡️"
  },
  {
    title: "Medical",
    details: "Precision parts for medical devices",
    icon: "⚕️"
  }
];

const Contact = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const formRef = useRef();

  useEffect(() => {
    emailjs.init("YOUR_PUBLIC_KEY"); // Replace with your actual public key
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await emailjs.send(
        "service_uhfg70h",
        "template_74vsqjx",
        {
          title: "New Enquiry",
          from_name: formData.name,
          from_email: formData.email,
          name: formData.name,
          message: formData.message,
          phone: formData.phone,
          email: formData.email
        },
        "eH_kIExYo_OdflTph" // Replace with your actual public key
      );

      setSnackbarMessage('Message sent successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error sending email:', error);
      setSnackbarMessage('Failed to send message. Please try again.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Hero Section */}
      <Box sx={{ mb: 8, textAlign: 'center' }}>
        <Typography 
          variant="h2" 
          sx={{ 
            color: '#1a472a',
            fontWeight: 'bold',
            mb: 3,
          }}
        >
          Let's Build Excellence Together
        </Typography>
        <Typography 
          variant="h5" 
          sx={{ 
            color: 'text.secondary',
            maxWidth: '800px',
            mx: 'auto',
            mb: 4,
            lineHeight: 1.8
          }}
        >
          Connect with Vibhuti Industries for precision engineering solutions that drive your success. Our expertise in manufacturing excellence ensures your components meet the highest standards.
        </Typography>
      </Box>

      {/* Why Connect With Us Section */}
      <Box sx={{ mb: 8 }}>
        <Typography 
          variant="h3" 
          sx={{ 
            textAlign: 'center', 
            mb: 6, 
            color: '#1a472a',
            fontWeight: 'bold',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -16,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 100,
              height: 4,
              background: 'linear-gradient(90deg, #1a472a 0%, #4caf50 100%)',
              borderRadius: '2px',
            }
          }}
        >
          Why Partner With Us
        </Typography>
        <Grid container spacing={4}>
          {whyConnectCards.map((card, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Zoom in={true} style={{ transitionDelay: `${index * 150}ms` }}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 4,
                    height: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: 8,
                      '& .icon': {
                        transform: 'scale(1.1)',
                      }
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: card.color,
                    }
                  }}
                >
                  <Box 
                    className="icon"
                    sx={{ 
                      color: card.color,
                      transition: 'transform 0.3s ease',
                      mb: 2
                    }}
                  >
                    {React.cloneElement(card.icon, { sx: { fontSize: 48 } })}
                  </Box>
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                    {card.title}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                    {card.description}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    {card.points.map((point, idx) => (
                      <Typography
                        key={idx}
                        variant="body2"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 1,
                          color: 'text.secondary',
                          '&::before': {
                            content: '"•"',
                            color: card.color,
                            fontSize: '1.5em',
                            mr: 1
                          }
                        }}
                      >
                        {point}
                      </Typography>
                    ))}
                  </Box>
                </Paper>
              </Zoom>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Industries We Serve */}
      <Box sx={{ mb: 8, textAlign: 'center' }}>
        <Typography 
          variant="h4" 
          sx={{ 
            mb: 4,
            color: '#1a472a',
            fontWeight: 'bold'
          }}
        >
          Industries We Serve
        </Typography>
        <Grid container spacing={2} justifyContent="center">
          {industriesServed.map((industry, index) => (
            <Grid item xs={6} sm={3} key={index}>
              <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 3,
                      '& .industry-icon': {
                        transform: 'scale(1.2)',
                      }
                    }
                  }}
                >
                  <Typography 
                    className="industry-icon"
                    sx={{ 
                      fontSize: '2.5rem',
                      mb: 1,
                      transition: 'transform 0.3s ease'
                    }}
                  >
                    {industry.icon}
                  </Typography>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {industry.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {industry.details}
                  </Typography>
                </Paper>
              </Zoom>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Contact Information Cards */}
      <Box sx={{ mb: 8 }}>
        <Typography 
          variant="h3" 
          sx={{ 
            textAlign: 'center', 
            mb: 6, 
            color: '#1a472a',
            fontWeight: 'bold',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -16,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 100,
              height: 4,
              background: 'linear-gradient(90deg, #1a472a 0%, #4caf50 100%)',
              borderRadius: '2px',
            }
          }}
        >
          Connect With Us
        </Typography>
        <Grid container spacing={3}>
          {contactCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                <Card
                  sx={{
                    height: '100%',
                    transition: 'all 0.3s ease',
                    cursor: card.link ? 'pointer' : 'default',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: card.color,
                    },
                  }}
                  onClick={() => card.link && window.open(card.link, '_blank')}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 2,
                        color: card.color,
                      }}
                    >
                      {React.cloneElement(card.icon, { sx: { fontSize: 40 } })}
                      <Typography
                        variant="h6"
                        sx={{
                          ml: 2,
                          fontWeight: 'bold',
                          color: 'inherit',
                        }}
                      >
                        {card.title}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    {card.details.map((detail, idx) => (
                      <Typography
                        key={idx}
                        variant="body1"
                        sx={{
                          mb: 1,
                          color: 'text.secondary',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        • {detail}
                      </Typography>
                    ))}
                    {card.link && (
                      <Button
                        variant="text"
                        endIcon={<LanguageIcon />}
                        sx={{
                          mt: 2,
                          color: card.color,
                          '&:hover': {
                            backgroundColor: `${card.color}10`,
                          },
                        }}
                      >
                        {card.linkText}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Quick Contact Buttons */}
      <Box sx={{ mb: 8, textAlign: 'center' }}>
        <Grid container spacing={2} justifyContent="center">
          <Grid item>
            <Tooltip title="Call Us" arrow>
              <IconButton
                component="a"
                href="tel:+919999922657"
                sx={{
                  bgcolor: '#1a472a',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#4caf50',
                  },
                  p: 2,
                }}
              >
                <PhoneIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          </Grid>
          <Grid item>
            <Tooltip title="Email Us" arrow>
              <IconButton
                component="a"
                href="mailto:office@thevibuti.com"
                sx={{
                  bgcolor: '#1a472a',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#4caf50',
                  },
                  p: 2,
                }}
              >
                <EmailIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          </Grid>
          <Grid item>
            <Tooltip title="Visit Our Location" arrow>
              <IconButton
                component="a"
                href="https://maps.google.com/?q=E-143+Sector-63+Noida"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  bgcolor: '#1a472a',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#4caf50',
                  },
                  p: 2,
                }}
              >
                <LocationOnIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      {/* Contact Form Section */}
      <Grid container spacing={4} alignItems="center">
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={3}
            sx={{
              p: 4,
              borderRadius: 2,
              background: 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #1a472a 0%, #4caf50 100%)',
              },
            }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                mb: 4,
                color: '#1a472a',
                fontWeight: 'bold',
                textAlign: 'center',
              }}
            >
              Start Your Project
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 4,
                color: 'text.secondary',
                textAlign: 'center'
              }}
            >
              Tell us about your requirements, and our team will get back to you within 24 hours with a comprehensive solution.
            </Typography>
            <form onSubmit={handleSubmit} ref={formRef}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#1a472a',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Message"
                    name="message"
                    multiline
                    rows={4}
                    value={formData.message}
                    onChange={handleInputChange}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    disabled={loading}
                    sx={{
                      mt: 2,
                      bgcolor: '#1a472a',
                      '&:hover': {
                        bgcolor: '#4caf50',
                      },
                      height: '50px',
                    }}
                    endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  >
                    {loading ? 'Sending...' : 'Send Message'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Grid>
        <Grid 
          item 
          xs={12} 
          md={6}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
          }}
        >
          <Fade in timeout={1000}>
            <Box
              sx={{
                width: '100%',
                height: isMobile ? '300px' : '500px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                transform: 'scale(1.2)',
              }}
            >
              <DotLottieReact
                src="https://lottie.host/8120eb4d-928f-4ab0-880d-8e2244d07e19/MDiEdBPEBu.lottie"
                loop
                autoplay
                style={{ width: '100%', height: '100%' }}
              />
            </Box>
          </Fade>
        </Grid>
      </Grid>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Contact;
